/*
================================================================================
FILE HEADER — apps/studio-web/src/lib/assist/buildStep.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                    | What Changed          |
|----|------------|-------------------|------------------------|-----------------------|
| 1  | 2026-07-02 | Claude Sonnet 5   | Phase 6 AI Assist mode | Created — converts a  |
|    |            |                   |                        | parsed AssistIntent + |
|    |            |                   |                        | resolved element into |
|    |            |                   |                        | ordinary IRStep(s)     |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/components/AssistBar.tsx
- apps/studio-web/src/lib/assist/__tests__/buildStep.test.ts

FUNCTIONS / EXPORTS COVERED
----------------------------
- buildAssistSteps        — (intent, resolved, tree) -> IRStep[] | null
- buildSelectorFromMatch  — (match, description, tree) -> Selector (exported for tests)

IMPORTANT POINTS
----------------
1. This is the COMMIT step of the AI Assist pipeline (AI_ASSIST_Research_Plan.md
   §3). The output is a 100%-ordinary IRStep — nothing downstream (runner,
   reporter, healing) needs to know the step originated from a typed sentence.
   Candidates are tagged `addedBy:'assist'` purely for audit-trail purposes.
2. `typeText` has NO selector field in the IR schema (packages/ir/src/schemas.ts)
   — it always types into whatever is currently focused. So "type X into the
   Email field" compiles to TWO steps: [tap(Email field), typeText(X)],
   mirroring how Drive-mode recording already captures click-then-type.
   "type X" with no target compiles to a single typeText step (relies on
   whatever is already focused, e.g. immediately after a manual tap).
3. Four intents (openUrl, wait, screenshot, assertPageContainsText) need NO
   element resolution at all — "page contains X" maps directly to a
   `textContains` candidate + assertion:'visible', with no live-tree lookup
   required (Playwright's getByText resolves it at replay time, same as any
   other textContains candidate).
4. Every other intent requires a `resolved` ResolvedMatch + the live `tree`
   (for fingerprint construction) — returns null if either is missing, which
   the caller (AssistBar) treats as "not ready to confirm yet".
================================================================================
*/

import { newId } from '@plex/ir';
import type { IRStep, Selector, Candidate } from '@plex/ir';
import type { SerializedAXTree } from '../../extension/protocol.js';
import type { AssistIntent } from './grammar.js';
import type { ResolvedMatch } from './resolver.js';
import { buildFingerprint } from './fingerprint.js';

/** Builds a Selector from a resolved ARIA node, tagging every candidate `addedBy:'assist'`. */
export function buildSelectorFromMatch(
  match: ResolvedMatch,
  description: string,
  tree: SerializedAXTree,
): Selector {
  const node = match.node;
  const candidates: Candidate[] = [];

  const attrs = node.attributes ?? {};
  const testId = attrs['data-testid'] ?? attrs['data-cy'] ?? attrs['data-qa'] ?? attrs['data-test'];
  if (testId) candidates.push({ strategy: 'a11yId', value: testId, addedBy: 'assist' });
  if (node.name) candidates.push({ strategy: 'text', value: node.name, addedBy: 'assist' });
  if (node.description && node.description !== node.name) {
    candidates.push({ strategy: 'contentDesc', value: node.description, addedBy: 'assist' });
  }
  if (node.cssSelector) candidates.push({ strategy: 'css', value: node.cssSelector, addedBy: 'assist' });
  if (candidates.length === 0) {
    candidates.push({ strategy: 'className', value: `${node.role}[${node.name ?? ''}]`, addedBy: 'assist' });
  }

  return {
    description,
    candidates,
    fingerprint: buildFingerprint(node, tree),
  };
}

/**
 * Converts a parsed AssistIntent (+ a resolved element, when the intent needs one)
 * into one or more ordinary IRStep objects. Returns null when the intent cannot
 * yet be committed (e.g. an element-targeting intent with no resolved match).
 *
 * `manualSelector` is the optional "🎯 Pick element" enrichment (AI_ASSIST_
 * Research_Plan.md §4): when the user manually clicks an element, its
 * candidates are prepended (tagged `addedBy:'user'`) onto the auto-resolved
 * selector's candidates for extra fallback strength. When there is NO
 * `resolved` match at all (e.g. Lane 1 found nothing), `manualSelector`
 * becomes the sole source of candidates instead of a merge.
 */
export function buildAssistSteps(
  intent: AssistIntent,
  resolved: ResolvedMatch | null,
  tree: SerializedAXTree | null,
  manualSelector?: Selector | null,
): IRStep[] | null {
  const raw = intent.raw;

  // ── Intents that need no element resolution ────────────────────────────────
  switch (intent.kind) {
    case 'openUrl': {
      if (!intent.url) return null;
      return [{ id: newId(), type: 'openUrl', label: `Open ${intent.url}`, url: intent.url }];
    }
    case 'wait': {
      if (intent.seconds === undefined) return null;
      return [{ id: newId(), type: 'wait', label: `Wait ${intent.seconds}s`, durationMs: Math.round(intent.seconds * 1000) }];
    }
    case 'screenshot': {
      return [{ id: newId(), type: 'screenshot', label: 'Take screenshot' }];
    }
    case 'assertPageContainsText': {
      if (!intent.text) return null;
      const selector: Selector = {
        description: raw,
        candidates: [{ strategy: 'textContains', value: intent.text, addedBy: 'assist' }],
      };
      return [{ id: newId(), type: 'assert', label: `Page contains "${intent.text}"`, selector, assertion: 'visible' }];
    }
    case 'typeText': {
      // Only handle the no-target form here; a targeted typeText falls through
      // to the resolved-element branch below (needs a preceding tap-to-focus).
      if (!intent.target) {
        if (intent.text === undefined) return null;
        return [{ id: newId(), type: 'typeText', label: `Type "${intent.text}"`, text: intent.text }];
      }
      break;
    }
    default:
      break;
  }

  // ── Everything else requires a resolved element+tree, a manual pick, or both ─
  if (!manualSelector && (!resolved || !tree)) return null;

  const selector: Selector = (() => {
    const manualCandidates = manualSelector
      ? manualSelector.candidates.map(c => ({ ...c, addedBy: 'user' as const }))
      : [];

    if (resolved && tree) {
      const auto = buildSelectorFromMatch(resolved, raw, tree);
      return manualCandidates.length > 0
        ? { ...auto, candidates: [...manualCandidates, ...auto.candidates] }
        : auto;
    }
    // No live resolution at all — the manual pick is the only source.
    return { description: raw, candidates: manualCandidates };
  })();

  const label = (intent.target ?? resolved?.accessibleName ?? 'element').trim();

  switch (intent.kind) {
    case 'tap':
      return [{ id: newId(), type: 'tap', label: `Tap ${label}`, selector }];
    case 'doubleTap':
      return [{ id: newId(), type: 'doubleTap', label: `Double-tap ${label}`, selector }];
    case 'longPress':
      return [{ id: newId(), type: 'longPress', label: `Long-press ${label}`, selector, durationMs: 500 }];
    case 'clearText':
      return [{ id: newId(), type: 'clearText', label: `Clear ${label}`, selector }];
    case 'waitForVisible':
      return [{ id: newId(), type: 'waitFor', label: `Wait for ${label} to be visible`, selector, state: 'visible' }];
    case 'scrollUntilVisible':
      return [{ id: newId(), type: 'scrollUntilVisible', label: `Scroll until ${label} is visible`, selector, direction: 'down', maxScrolls: 10 }];
    case 'assertVisible':
      return [{ id: newId(), type: 'assert', label: `Assert ${label} is visible`, selector, assertion: 'visible' }];
    case 'assertNotVisible':
      return [{ id: newId(), type: 'assert', label: `Assert ${label} is hidden`, selector, assertion: 'notVisible' }];
    case 'assertEnabled':
      return [{ id: newId(), type: 'assert', label: `Assert ${label} is enabled`, selector, assertion: 'enabled' }];
    case 'assertDisabled':
      return [{ id: newId(), type: 'assert', label: `Assert ${label} is disabled`, selector, assertion: 'disabled' }];
    case 'assertHasText': {
      if (!intent.text) return null;
      return [{ id: newId(), type: 'assert', label: `Assert ${label} has text "${intent.text}"`, selector, assertion: 'hasText', value: intent.text }];
    }
    case 'typeText': {
      // Reached only when intent.target was set — tap to focus, then type.
      if (intent.text === undefined) return null;
      const tapStep: IRStep = { id: newId(), type: 'tap', label: `Tap ${label}`, selector };
      const typeStep: IRStep = { id: newId(), type: 'typeText', label: `Type "${intent.text}"`, text: intent.text };
      return [tapStep, typeStep];
    }
    default:
      return null;
  }
}
